/**
 * SetAssignmentsList — renders a list of `target = value` rows for the UPDATE
 * renderer's SET clause. Each row carries a non-editable target span + value
 * span + `×`; the section ships a `[+ add assignment]` footer.
 *
 * VQE-Edit wiring: when `editContext` is supplied the per-row `×` commits
 * `removeUpdateAssignment`; the column name opens the column picker and swaps
 * the column (`modify-assignment-target`, value kept); the value cell becomes
 * an inline-editable pill (`AssignmentEditableValuePill` →
 * `modifyUpdateAssignment`); the footer `[+ add assignment]` opens the
 * `AssignmentAddPopover` flow. A column the written table doesn't have (after
 * a Change table, or a typo) carries a warning glyph. Without `editContext`
 * the disabled VQE-RO scaffolding renders unchanged.
 *
 * Row edits optionally re-route through `mergeBranch` (MERGE branch SET lists)
 * or `conflictTail` (INSERT ON CONFLICT / ON DUPLICATE tails, §2.4 S6b) — one
 * seam, three routers. Conflict tails additionally guard the LAST assignment
 * (an empty SET/UPDATE list is ill-formed SQL — decision-5 style, tooltip
 * explains why).
 *
 * Reuses the shared `parseAssignmentsList` parser. Visually identical to
 * `../insert/AssignmentsList`; the future Scaffolding Common Controls slice
 * may consolidate the two into a shared `render/common/AssignmentsList`.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { ConflictTailTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { removeUpdateAssignment } from '../../edit/edits/removeUpdateAssignment';
import { removeInsertConflictAssignment } from '../../edit/edits/insertConflictAssignments';
import {
  removeMergeUpdateAssignment,
  type MergeBranchSelector,
} from '../../edit/edits/modifyMergeBranch';
import { AddButton } from '../common/AddButton';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { parseAssignmentsList } from '../insert/parseAssignmentsList';
import { modifyAssignmentTarget } from '../../edit/edits/modifyColumnTargets';
import { AssignmentAddPopover } from './AssignmentAddPopover';
import { AssignmentEditableValuePill, ValueComment } from './AssignmentEditableValuePill';
import { splitColumnRef } from '../../../../sql-resolver/scope/columnRefs';
import { findColumn, resolveWrittenTableColumns, tableForRef } from './assignmentColumns';
import { DmlColumnPicker } from './DmlColumnPicker';
import { UnknownColumnMark } from './UnknownColumnMark';
import { vqeCopy } from '../../ux/vqeCopy';

export interface SetAssignmentsListProps {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
  /**
   * Optional VQE-Edit context. When undefined the disabled scaffolding
   * renders unchanged. When supplied the row `×` and inline value editing are
   * wired through the `*UpdateAssignment` edit-action modules.
   */
  readonly editContext?: VqeEditContext;
  /**
   * When supplied, edits route through the MERGE-branch-aware ops instead of
   * the top-level UPDATE ops. The picked branch's `updateAssignments` is the
   * target list. Used by `WhenBranchCard` to drive nested SET edits inside a
   * `WHEN [NOT] MATCHED THEN UPDATE …` arm without mis-targeting the (empty)
   * top-level UPDATE assignment list.
   */
  readonly mergeBranch?: MergeBranchSelector;
  /**
   * When supplied, edits route through the tail-discriminated
   * `*-insert-conflict-assignment` ops targeting an INSERT structured tail
   * (`'on-conflict'` / `'on-duplicate'`) — the third router beside
   * `mergeBranch` (§2.4 S6b).
   */
  readonly conflictTail?: ConflictTailTarget;
  /**
   * EDG-29 — display the assignment TARGET unqualified (`Quantity`, not
   * `t.Quantity`). One consistent rule across engines: mssql qualifies MERGE
   * UPDATE targets (`t.Quantity = …`) while pg leaves them bare
   * (`first_name = …`); the target table is unambiguous in a MERGE assignment,
   * so we show it bare everywhere. Purely cosmetic — the emitted SQL keeps its
   * original qualifier (the target span is read-only; only the value edits).
   */
  readonly displayTargetsUnqualified?: boolean;
}

export function SetAssignmentsList({
  bufferText,
  start,
  end,
  options,
  testIdPrefix,
  editContext,
  mergeBranch,
  conflictTail,
  displayTargetsUnqualified,
}: SetAssignmentsListProps) {
  const rows = parseAssignmentsList(bufferText, start, end, options);
  const editEnabled = !!editContext;
  const router = { mergeBranch, conflictTail };
  const table = editContext ? resolveWrittenTableColumns(editContext, router) : null;
  const targets = rows.map((row) => row.target);
  // Conflict tails refuse to empty the list (ill-formed SQL) — decision 5.
  const lastConflictRow = !!conflictTail && rows.length === 1;
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testIdPrefix}-assignments`}>
      {rows.length === 0 ? (
        <p
          className="text-xs italic text-muted-foreground"
          data-testid={`${testIdPrefix}-assignments-empty`}
        >
          No assignments parsed.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map((row, idx) => {
            const label = displayTargetsUnqualified ? unqualifyTarget(row.target) : row.target;
            // A target qualified by another table (MySQL `UPDATE a JOIN b … SET b.x`) writes b: unknown columns.
            const rowTable = table ? tableForRef(table, row.target) : null;
            return (
            <li
              key={`${row.start}-${idx}`}
              className="flex items-baseline gap-2 rounded-md border border-border bg-muted/30 px-2 py-1"
              data-testid={`${testIdPrefix}-assignment-${idx}`}
            >
              {editContext && rowTable ? (
                // The column is its own picker: picking swaps the column and
                // keeps the value; the row's qualifier (`t.` in `t.qty`)
                // carries over.
                <span className="flex min-w-0 shrink-0 items-center gap-1 self-center">
                  <DmlColumnPicker
                    testId={`${testIdPrefix}-assignment-target-${idx}`}
                    table={rowTable}
                    takenRefs={targets}
                    qualifier={splitColumnRef(row.target).qualifier}
                    usage="set"
                    triggerLabel={label}
                    triggerVariant="inline"
                    triggerClassName="font-mono text-xs font-semibold"
                    onPick={({ text }) => {
                      void modifyAssignmentTarget(idx, text, router, toCommitCtx(editContext));
                    }}
                  />
                  <UnknownColumnMark
                    columnRef={row.target}
                    table={rowTable}
                    testId={`${testIdPrefix}-assignment-target-${idx}-unknown`}
                  />
                </span>
              ) : (
                <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
                  <span
                    className="font-mono text-xs font-semibold"
                    data-testid={`${testIdPrefix}-assignment-target-${idx}`}
                  >
                    {label}
                  </span>
                </VqeTooltip>
              )}
              <span aria-hidden className="text-muted-foreground/70">=</span>
              {editEnabled && editContext ? (
                <AssignmentEditableValuePill
                  testId={`${testIdPrefix}-assignment-value-${idx}`}
                  text={row.expression}
                  comment={row.comment}
                  table={rowTable}
                  column={rowTable ? findColumn(rowTable, row.target) : undefined}
                  index={idx}
                  editContext={editContext}
                  mergeBranch={mergeBranch}
                  conflictTail={conflictTail}
                />
              ) : (
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
                    <span
                      className="min-w-0 whitespace-pre-wrap break-words font-mono text-xs"
                      data-testid={`${testIdPrefix}-assignment-value-${idx}`}
                    >
                      {row.expression}
                    </span>
                  </VqeTooltip>
                  <ValueComment text={row.comment} testId={`${testIdPrefix}-assignment-value-${idx}-comment`} />
                </span>
              )}
              <RemoveButton
                testId={`${testIdPrefix}-assignment-remove-${idx}`}
                ariaLabel={`Remove assignment ${idx + 1}`}
                className="self-center"
                disabled={!editEnabled || lastConflictRow}
                tooltip={
                  !editEnabled
                    ? undefined
                    : lastConflictRow
                      ? vqeCopy.disabled.conflictAssignmentsLastOne
                      : `Remove assignment ${idx + 1}`
                }
                onClick={
                  editEnabled && !lastConflictRow && editContext
                    ? () => {
                        const ctx = toCommitCtx(editContext);
                        if (conflictTail) {
                          removeInsertConflictAssignment(idx, conflictTail, ctx, {
                            structure: safeExtractStructure(editContext),
                          }).catch(recordEditFailure);
                        } else if (mergeBranch) {
                          void removeMergeUpdateAssignment(mergeBranch, idx, ctx);
                        } else {
                          void removeUpdateAssignment(idx, ctx);
                        }
                      }
                    : undefined
                }
              />
            </li>
            );
          })}
        </ul>
      )}
      {/* items-start: the add button keeps its own width (stretched, it would
          centre its label apart from every other card's left-aligned add
          action); the draft row that replaces it spans the list (w-full). */}
      <div className="flex flex-col items-start">
        {editEnabled ? (
          <AssignmentAddPopover
            editContext={editContext}
            testIdPrefix={testIdPrefix}
            mergeBranch={mergeBranch}
            conflictTail={conflictTail}
            existingTargets={targets}
          />
        ) : (
          <AddButton label={vqeCopy.actions.addAssignment} testId={`${testIdPrefix}-add-assignment`} />
        )}
      </div>
    </div>
  );
}

/**
 * EDG-29 cosmetic: drop a single leading `alias.` qualifier from an assignment
 * target for DISPLAY only. Deliberately narrow — it strips just the simple
 * unquoted `alias.col` shape and leaves anything fancier (quoted identifiers,
 * multi-part names) untouched, so it can never mangle a target it doesn't
 * fully understand.
 */
function unqualifyTarget(target: string): string {
  const match = /^[A-Za-z_][\w$]*\.([A-Za-z_][\w$]*)$/.exec(target.trim());
  return match ? match[1] : target;
}
